import { ChordQuality } from '../../types';
import { ChordBuilder } from './harmony/ChordBuilder';
import { HarmonicColor } from './harmony/HarmonicColor';
import { VoiceLeading } from './harmony/VoiceLeading';
import { HarmonyUtils } from '../../utils/music/HarmonyUtils';
import { HarmonicStorytelling, StorytellingDecision } from './harmony/HarmonicStorytelling';
import { ResolutionManager } from './harmony/ResolutionManager';
import { CadentialVoicing } from './harmony/CadentialVoicing';
import { ProgressionChord } from '../../utils/music/composerProgressions';
import { ComposerSession } from './ComposerSession';
import { CadenceType } from './StructureEngine';

/**
 * HarmonyEngine
 * COORDINATOR for Harmonic Logic.
 * Delegates to specialized sub-modules to keep things clean.
 * 
 * V11 ENHANCEMENT:
 * - HarmonicStorytelling: Deceptive cadences, key lifts, tritone substitutions
 */
export class HarmonyEngine {
    private chordBuilder: ChordBuilder;
    private harmonicColor: HarmonicColor;
    private voiceLeading: VoiceLeading;

    // V11: Harmonic Storytelling for surprise cadences and key changes
    private storytelling: HarmonicStorytelling;

    // V12.5: Resolution Manager (Modularized)
    private resolutionManager: ResolutionManager;

    // P3 FIX: Cadential Voicing for proper phrase endings
    private cadentialVoicing: CadentialVoicing;

    constructor() {
        this.chordBuilder = new ChordBuilder();
        this.harmonicColor = new HarmonicColor();
        this.voiceLeading = new VoiceLeading();
        this.storytelling = new HarmonicStorytelling();
        this.resolutionManager = new ResolutionManager();
        this.cadentialVoicing = new CadentialVoicing();
    }

    public reset() {
        this.voiceLeading.reset();
        this.storytelling.reset();
        // P0 FIX: Reset resolution manager state
        this.resolutionManager.reset();
        // P3 FIX: Reset cadential voicing
        this.cadentialVoicing.reset();
    }

    // --- HARMONIC MEMORY (Forecast System) ---

    /**
     * Ensures we have 5 chords in the forecast.
     * Uses "The Rule of Resolution" (Lookahead).
     */
    public ensureForecast(
        session: any, // Typed as ComposerSession
        scaleNotes: number[],
        conclusionState: 'neutral' | 'preparation' | 'cadence' | 'finale' = 'neutral',
        touchesRemaining: number = 100 // V12.5: Stop filling if near end
    ) {
        if (!session.progression || !session.progression.degrees) {
            return;
        }

        // P0 FIX: If we are in the "Resolution Zone" (last 12 touches),
        // STOP generating standard forecast. The resolution logic will handle it using updateResolutionForecast.
        if (this.resolutionManager.shouldActivateResolution(touchesRemaining)) {
            return;
        }

        // 1. Initialize History if empty (Bootstrap)
        if (session.chordHistory.length === 0) {
            const initialDegree = session.progression.degrees[session.progressionIndex] || 1;
            const { degree, quality } = this.getSafeChordDegree(initialDegree, session.scale);
            const rootMidi = this.findRootMidi(scaleNotes, degree, session.octaveOffset);
            const notes = this.getChromaticChord(rootMidi, quality);

            session.chordHistory.push({
                degree,
                quality,
                notes,
                rootMidi,
                isSecondaryDominant: false
            });
        }

        // 2. Refill Forecast
        let safetyCounter = 0;
        while (session.chordForecast.length < 5 && safetyCounter < 10) {
            this.generateNextForecastItem(session, scaleNotes, conclusionState);
            safetyCounter++;
        }
    }

    /**
     * P0 FIX: EXTENDED LOOKAHEAD RESOLUTION
     * Aggressively rewrites the chord forecast when approaching the end of the song
     * to ensure a perfect landing on the Tonic.
     * Called by MaestroVoice when touchesRemaining <= 12.
     */
    public updateResolutionForecast(session: ComposerSession, touchesRemaining: number, scaleNotes: number[]) {
        this.resolutionManager.updateResolutionForecast(session, touchesRemaining, scaleNotes);
    }

    /**
     * P0 FIX: Get pre-cadential preparation chord based on song progress
     */
    public getPreCadentialChord(
        songProgress: number,
        scaleNotes: number[],
        progressionComplexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic'
    ): { degree: number; quality: ChordQuality } | null {
        return this.resolutionManager.getPreCadentialChord(songProgress, scaleNotes, progressionComplexity);
    }

    /**
     * P0 FIX: Check if ritardando (slowdown) should be active
     */
    public isRitardandoActive(): boolean {
        return this.resolutionManager.isRitardandoActive();
    }

    /**
     * P0 FIX: Get the resolution lookahead constant for external use
     */
    public getResolutionLookahead(): number {
        return ResolutionManager.RESOLUTION_LOOKAHEAD;
    }

    private generateNextForecastItem(session: ComposerSession, scaleNotes: number[], conclusionState: 'neutral' | 'preparation' | 'cadence' | 'finale') {
        const lastChord = session.chordForecast.length > 0
            ? session.chordForecast[session.chordForecast.length - 1]
            : session.chordHistory[session.chordHistory.length - 1];

        // Robustness check
        if (!session.progression || !session.progression.degrees || session.progression.degrees.length === 0) return;

        // V12.5 PROFESSIONAL FINALE LOGIC
        if (conclusionState === 'finale') {
            // THE ARRIVAL: Force perfect stability (Tonic I)
            this.pushForecastItem(session, 0, 'major', scaleNotes, false);
            return;
        }

        if (conclusionState === 'cadence') {
            // THE TURNAROUND: Force Dominant V7 to create maximum pull to Tonic
            // (Simplification of Cadential 6/4 -> V7)
            this.pushForecastItem(session, 4, 'dominant7', scaleNotes, false);
            return;
        }

        const virtualIndex = (session.progressionIndex + session.chordForecast.length + 1) % session.progression.degrees.length;
        const rawStep = session.progression.degrees[virtualIndex] ?? 0;

        let degree: number;
        let quality: any; // ChordQuality type
        let isExplicitlyBorrowed = false;

        if (typeof rawStep === 'number') {
            // Standard numerical degree
            const safe = this.getSafeChordDegree(rawStep, session.scale);
            degree = safe.degree;
            quality = safe.quality;
        } else {
            // Explicit chord object (V11)
            const step = rawStep as ProgressionChord;
            degree = step.degree;
            isExplicitlyBorrowed = !!step.isBorrowed;

            // If quality is explicit, use it. Otherwise calculate safe quality.
            if (step.quality) {
                quality = step.quality;
            } else {
                quality = this.getSafeChordDegree(degree, session.scale).quality;
            }
        }

        let isSecondaryDominant = false;
        // Logic for borrowing
        let rootMidi: number;

        // Check for borrowed chord
        const borrowed = this.getBorrowedChord(degree, session.scale, this.findRootMidi(scaleNotes, degree, session.octaveOffset), isExplicitlyBorrowed);

        if (borrowed.isBorrowed) {
            // If borrowed, use the borrowed quality and root
            if (borrowed.quality && !isExplicitlyBorrowed) quality = borrowed.quality; // Don't override explicit quality if set
            if (borrowed.rootMidi) rootMidi = borrowed.rootMidi;
            else rootMidi = this.findRootMidi(scaleNotes, degree, session.octaveOffset);
        } else {
            // Standard root
            rootMidi = this.findRootMidi(scaleNotes, degree, session.octaveOffset);
        }

        // V12.5 PREPARATION PHASE (Dominant Pedal)
        if (conclusionState === 'preparation') {
            // "The Setup": Force the bass/root to be the Dominant (V)
            // creating a pedal point tension regardless of the chord above.
            // Note: This changes the voicing to be "Chord / V" (Slash Chord) effect
            rootMidi = this.findRootMidi(scaleNotes, 4, session.octaveOffset); // 4 = 5th scale degree (0-indexed)

            // Optional: Simplify quality to avoid clashes over pedal
            if (quality === 'diminished') quality = 'minor';
        }

        let notes: number[] = [];

        // Resolve Notes
        notes = this.getChromaticChord(rootMidi, quality);

        session.chordForecast.push({
            degree,
            quality,
            notes,
            rootMidi,
            isSecondaryDominant: false
        });
    }

    private pushForecastItem(session: any, degree: number, quality: ChordQuality, scaleNotes: number[], isSecondaryDominant: boolean) {
        const rootMidi = this.findRootMidi(scaleNotes, degree, session.octaveOffset);
        const notes = this.getChromaticChord(rootMidi, quality);
        session.chordForecast.push({
            degree,
            quality,
            notes,
            rootMidi,
            isSecondaryDominant
        });
    }

    /**
     * Advances the chord progression: Forecast -> History
     */
    public advanceChord(session: any): any {
        if (session.chordForecast.length === 0) return null;

        const nextChord = session.chordForecast.shift();
        session.chordHistory.push(nextChord);

        // Keep History capped at 5
        if (session.chordHistory.length > 5) {
            session.chordHistory.shift();
        }

        return nextChord;
    }

    public getCurrentChord(session: any): any {
        if (session.chordHistory.length === 0) return null;
        return session.chordHistory[session.chordHistory.length - 1];
    }

    private findRootMidi(scaleNotes: number[], degree: number, octaveOffset: number): number {
        return HarmonyUtils.findRootMidi(scaleNotes, degree, octaveOffset);
    }

    // --- DELEGATE TO VOICE LEADING ---

    public getVoiceLedChord(targetNotes: number[], mood: 'Happy' | 'Sad' | 'Energetic' | 'Calm' | 'Mystery' = 'Calm', phraseFunction: 'Tension' | 'Resolution' | 'Neutral' = 'Neutral'): number[] {
        return this.voiceLeading.getVoiceLedChord(targetNotes, mood, phraseFunction);
    }

    public getSmartBassNote(
        scaleNotes: number[],
        rootNoteMidi: number,
        chordQuality: 'major' | 'minor' | 'diminished' | 'augmented' | 'sus2' | 'sus4' | 'dominant7' | 'minor7' = 'major',
        lastBassNote: number | null = null
    ): number {
        return this.voiceLeading.getSmartBassNote(scaleNotes, rootNoteMidi, chordQuality, lastBassNote);
    }

    public getSuspendedChord(
        chordNotes: number[],
        scaleNotes: number[],
        quality: ChordQuality,
        rootMidi: number,
        phraseFunction: 'Tension' | 'Resolution' | 'Neutral' = 'Neutral'
    ): { mainNotes: number[], resolutionNotes: { note: number, delayRatio: number }[] } {
        return this.voiceLeading.getSuspendedChord(chordNotes, scaleNotes, quality, rootMidi, phraseFunction);
    }

    // --- DELEGATE TO CADENTIAL VOICING (P3 FIX) ---

    /**
     * P3 FIX: Get proper cadential voicing for phrase endings
     */
    public getCadentialVoicing(
        chordNotes: number[],
        rootMidi: number,
        quality: ChordQuality,
        cadenceType: CadenceType,
        touchesRemaining: number
    ): { voicedNotes: number[]; bassNote: number; isRootPosition: boolean } {
        return this.cadentialVoicing.getCadentialVoicing(chordNotes, rootMidi, quality, cadenceType, touchesRemaining);
    }

    public getSafeChordDegree(degree: number, scaleType: string, allowDissonanceProb: number = 0.05): { degree: number, quality: ChordQuality } {
        return this.harmonicColor.getSafeChordDegree(degree, scaleType, allowDissonanceProb);
    }

    public getBorrowedChord(
        degree: number,
        scaleType: string,
        rootMidi: number,
        force: boolean = false
    ): { isBorrowed: boolean, quality?: ChordQuality, rootMidi?: number } {
        return this.harmonicColor.getBorrowedChord(degree, scaleType, rootMidi, force);
    }

    public getSecondaryDominant(
        targetDegree: number,
        scaleType: string,
        rootMidi: number
    ): { notes: number[], root: number } | null {
        return this.harmonicColor.getSecondaryDominant(targetDegree, scaleType, rootMidi);
    }

    /**
     * V9: Delegate for tension-aware chord modification.
     */
    public getTensionChordModification(
        quality: ChordQuality,
        tensionPhase: 'tension' | 'release' | 'neutral'
    ): ChordQuality {
        return this.harmonicColor.getTensionChordModification(quality, tensionPhase);
    }

    // --- DELEGATE TO CHORD BUILDER ---

    public getChromaticChord(rootMidi: number, quality: ChordQuality): number[] {
        return this.chordBuilder.getChromaticChord(rootMidi, quality);
    }

    public getChordNotes(scaleNotes: number[], rootScaleIndex: number, quality: ChordQuality, scaleType: string, rootMidi: number): number[] {
        return this.chordBuilder.getChordNotes(scaleNotes, rootScaleIndex, quality, scaleType, rootMidi);
    }

    // ===========================================================
    // V11: HARMONIC STORYTELLING METHODS
    // ===========================================================

    /**
     * V11: Get storytelling decision for current harmonic context.
     * Returns decisions about deceptive cadences, pivots, lifts, and tritone subs.
     */
    public getStorytellingDecision(
        currentDegree: number,
        currentQuality: string,
        targetDegree: number,
        currentSection: 'Verse' | 'Chorus' | 'Bridge',
        intensity: number,
        progressionComplexity: 'Simple' | 'Medium' | 'Complex' | 'Exotic' // V12.5 Argument
    ): StorytellingDecision {
        return this.storytelling.getDecision(
            currentDegree,
            currentQuality,
            targetDegree,
            currentSection,
            intensity,
            progressionComplexity
        );
    }

    /**
     * V11: Apply deceptive cadence to get alternate resolution target.
     */
    public applyDeceptiveCadence(
        originalDegree: number,
        deceptiveTarget: number,
        scaleNotes: number[]
    ): number {
        return this.storytelling.applyDeceptiveCadence(originalDegree, deceptiveTarget, scaleNotes);
    }

    /**
     * V11: Notify storytelling module of section change.
     */
    public onSectionChange(newSection: 'Verse' | 'Chorus' | 'Bridge'): void {
        this.storytelling.onSectionChange(newSection);
    }

    /**
     * V11: Apply key lift to session (modulate up by semitones).
     * Returns the new key offset to apply.
     */
    public applyKeyLift(
        currentKeyOffset: number,
        liftAmount: number
    ): number {
        const newOffset = currentKeyOffset + liftAmount;
        if (process.env.NODE_ENV === 'development') {
            console.log(`[V11] Key Lift: +${liftAmount} semitones (new offset: ${newOffset})`);
        }
        return newOffset;
    }

    /**
     * V11: Get tritone substitution chord.
     * Replaces V chord with bII7 chord.
     */
    public getTritoneSubstitution(
        currentRootMidi: number
    ): { notes: number[], rootMidi: number } {
        // Tritone is 6 semitones down from V
        const tritoneRoot = currentRootMidi - 6;
        // Build dominant 7th chord on tritone root
        const notes = this.getChromaticChord(tritoneRoot, 'dominant7');
        return { notes, rootMidi: tritoneRoot };
    }
}

